"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ApaHeading,
  buildApaHeadings,
  type HeadingResult,
  type HeadingWarningKind,
  headingsToHtml,
  headingsToText,
} from "@/lib/tool-kit/paper-formatting/apa-headings";

/** English strings. A translated content file overrides these through `ui`. */
export const APA_HEADING_GENERATOR_STRINGS = {
  outlineLabel: "Your headings",
  outlinePlaceholder:
    "# Method\n## Participants\n## Materials\n### Questionnaires\n# Results",
  outlineHint:
    "One heading per line. Give the level with # signs (# Level 1, ## Level 2 …), an outline number such as 2.1, or indentation.",
  titleCase: "Convert to APA title case",
  keepNumbers: "Keep outline numbers (APA headings are unnumbered)",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    "# Introduction\n# Method\n## Participants\n## Materials and procedure\n#### Sleep quality index\n## Data analysis\n# Results\n## descriptive statistics for the sample\n### Missing data\n## Associations with grades\n# Discussion",
  emptyPrompt: "Type or paste your headings to see them in APA format.",
  results: "Your headings in APA format",
  previewLabel: "Preview of the formatted headings",
  levelShort: "L{level}",
  levelName: "Level {level}",
  runIn: "Your paragraph begins here, on the same line.",
  copyRich: "Copy for Word or Google Docs",
  copyPlain: "Copy as plain text",
  copied: "Copied!",
  copyHint:
    "The rich copy keeps the centering, bold, italics, the half-inch indent of Levels 4 and 5 and their closing period. Your document’s own font and double spacing still apply.",
  warningsHeading: "Check these",
  warnings: {
    "first-not-level-1":
      "“{heading}” is the first heading but uses Level {level}. Start at Level 1 — APA uses the levels in order, from Level 1 down.",
    "skipped-level":
      "“{heading}” jumps from Level {previous} to Level {level}. Use Level {expected} here — APA does not skip levels.",
    "single-subsection":
      "“{heading}” is the only Level {level} heading under “{parent}”. Use at least two subsections or none, as in an outline.",
    introduction:
      "“{heading}” is not needed. APA papers do not label the introduction: the paper’s title at the top of the first page works as its Level 1 heading.",
    numbered:
      "“{heading}” keeps its outline number. APA headings are not numbered or lettered, so leave numbers out unless your instructor asks for them.",
    "too-deep":
      "“{heading}” is at level {level}, but APA has five levels, so it is shown as Level 5. Consider splitting the section instead.",
  } satisfies Record<HeadingWarningKind, string>,
  numbersRemoved:
    "Outline numbers such as 2.1 were left out of the headings, because APA headings are not numbered.",
  noWarnings:
    "The levels run in order from Level 1, and no section has a lone subsection.",
} as const;

type Strings = typeof APA_HEADING_GENERATOR_STRINGS;

/** Paragraph classes and heading-text classes for each level. */
const LEVEL_CLASS: Record<ApaHeading["level"], [string, string]> = {
  1: ["text-center", "font-bold"],
  2: ["", "font-bold"],
  3: ["", "font-bold italic"],
  4: ["indent-12", "font-bold"],
  5: ["indent-12", "font-bold italic"],
};

function PreviewHeading({
  heading,
  strings,
}: {
  heading: ApaHeading;
  strings: Strings;
}) {
  const [paragraph, text] = LEVEL_CLASS[heading.level];
  return (
    <div className="flex items-baseline gap-3">
      <span
        className="w-7 shrink-0 font-sans text-muted-foreground text-xs"
        title={t(strings.levelName, { level: heading.level })}
      >
        {t(strings.levelShort, { level: heading.level })}
      </span>
      <p className={`min-w-0 flex-1 leading-loose ${paragraph}`}>
        <span className={text}>{heading.text}</span>
        {heading.level >= 4 ? (
          <span className="text-muted-foreground"> {strings.runIn}</span>
        ) : null}
      </p>
    </div>
  );
}

function Warnings({
  result,
  strings,
}: {
  result: HeadingResult;
  strings: Strings;
}) {
  if (result.warnings.length === 0 && !result.numbersRemoved) {
    return <Notice>{strings.noWarnings}</Notice>;
  }
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
      <ul className="list-disc space-y-2 pl-5 text-foreground text-sm leading-relaxed">
        {result.warnings.map((warning) => (
          <li key={`${warning.kind}-${warning.line}`}>
            {t(
              strings.warnings[warning.kind],
              Object.fromEntries(
                Object.entries(warning.values).map(([key, value]) => [
                  key,
                  String(value),
                ])
              )
            )}
          </li>
        ))}
        {result.numbersRemoved ? <li>{strings.numbersRemoved}</li> : null}
      </ul>
    </div>
  );
}

function Results({
  result,
  strings,
}: {
  result: HeadingResult;
  strings: Strings;
}) {
  const html = headingsToHtml(result.headings);
  const text = headingsToText(result.headings);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <section
        aria-label={strings.previewLabel}
        className="space-y-1 rounded-lg border border-border bg-card p-4 font-serif text-foreground sm:p-6"
      >
        {result.headings.map((heading) => (
          <PreviewHeading
            heading={heading}
            key={`${heading.line}-${heading.text}`}
            strings={strings}
          />
        ))}
      </section>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          html={html}
          label={strings.copyRich}
          text={text}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyPlain}
          text={text}
        />
      </div>
      <p className={HINT}>{strings.copyHint}</p>
      <Warnings result={result} strings={strings} />
    </section>
  );
}

export default function ApaHeadingGeneratorWidget() {
  const strings = useToolUi(APA_HEADING_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [outline, setOutline] = useState("");
  const [titleCase, setTitleCase] = useState(true);
  const [keepNumbers, setKeepNumbers] = useState(false);
  const deferred = useDeferredValue(outline);
  const result = useMemo(
    () =>
      deferred.trim()
        ? buildApaHeadings(deferred, { titleCase, keepNumbers, locale })
        : null,
    [deferred, titleCase, keepNumbers, locale]
  );

  return (
    <>
      <TextAreaField
        hint={strings.outlineHint}
        id="aphg-outline"
        label={strings.outlineLabel}
        onChange={setOutline}
        placeholder={strings.outlinePlaceholder}
        rows={10}
        value={outline}
      />
      <div className="space-y-2">
        <Checkbox
          checked={titleCase}
          label={strings.titleCase}
          onChange={setTitleCase}
        />
        <Checkbox
          checked={keepNumbers}
          label={strings.keepNumbers}
          onChange={setKeepNumbers}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setOutline(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={outline.length === 0}
          onClick={() => setOutline("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {result && result.headings.length > 0 ? (
        <Results result={result} strings={strings} />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
